import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ReplaceProfilePayload } from '../types'

export function emptyPayload(): ReplaceProfilePayload {
  return {
    name: '',
    project_number: '',
    sender_gln: '', sender_name: '',
    receiver_gln: '', receiver_name: '',
    invoicer_gln: '', invoicer_name: '',
    invoicee_gln: '', invoicee_name: '',
    delivery_party_gln: '', delivery_party_name: '',
    ship_from_gln: '', ship_from_name: '',
  }
}

export function ErrorBanner({ msg, onDismiss }: { msg: string; onDismiss: () => void }) {
  return (
    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-300 rounded-lg px-4 py-2 text-sm flex justify-between items-center">
      {msg}
      <button onClick={onDismiss} className="ml-4 text-red-400 hover:text-red-600">✕</button>
    </div>
  )
}

export function SuccessBanner({ msg, onDismiss }: { msg: string; onDismiss: () => void }) {
  return (
    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 text-green-700 dark:text-green-300 rounded-lg px-4 py-2 text-sm flex justify-between items-center">
      {msg}
      <button onClick={onDismiss} className="ml-4 text-green-400 hover:text-green-600">✕</button>
    </div>
  )
}

interface ProfileFormProps {
  initial: ReplaceProfilePayload
  onSubmit: (data: ReplaceProfilePayload) => Promise<void>
  onCancel: () => void
  submitLabel: string
}

export function ProfileForm({ initial, onSubmit, onCancel, submitLabel }: ProfileFormProps) {
  const { t } = useTranslation()
  const [form, setForm] = useState<ReplaceProfilePayload>(initial)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const set = (field: keyof ReplaceProfilePayload, value: string) =>
    setForm((f) => ({ ...f, [field]: value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!form.name.trim()) { setError(t('replace_profile.error_name_required')); return }
    setLoading(true)
    try {
      await onSubmit(form)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('replace_profile.error_save_failed'))
    } finally {
      setLoading(false)
    }
  }

  const labelCls = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1'
  const inputCls = 'w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-300'

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <ErrorBanner msg={error} onDismiss={() => setError(null)} />}
      <div>
        <label className={labelCls}>{t('replace_profile.field_profile_name')} *</label>
        <input type="text" className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder={t('replace_profile.placeholder_profile_name')} maxLength={100} required />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>{t('replace_profile.field_project_number')}</label>
          <input type="text" className={inputCls} value={form.project_number ?? ''} onChange={(e) => set('project_number', e.target.value)} placeholder={t('replace_profile.placeholder_project_number')} maxLength={50} />
        </div>
        <div className="col-span-2 border-t border-gray-100 dark:border-gray-800 pt-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('replace_profile.section_sender')}</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t('replace_profile.field_sender_gln')}</label>
              <input type="text" className={inputCls} value={form.sender_gln ?? ''} onChange={(e) => set('sender_gln', e.target.value)} placeholder={t('replace_profile.placeholder_sender_gln')} maxLength={50} />
            </div>
            <div>
              <label className={labelCls}>{t('replace_profile.field_sender_name')}</label>
              <input type="text" className={inputCls} value={form.sender_name ?? ''} onChange={(e) => set('sender_name', e.target.value)} placeholder={t('replace_profile.placeholder_sender_name')} maxLength={200} />
            </div>
          </div>
        </div>
        <div className="col-span-2 border-t border-gray-100 dark:border-gray-800 pt-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('replace_profile.section_receiver')}</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t('replace_profile.field_receiver_gln')}</label>
              <input type="text" className={inputCls} value={form.receiver_gln ?? ''} onChange={(e) => set('receiver_gln', e.target.value)} placeholder={t('replace_profile.placeholder_receiver_gln')} maxLength={50} />
            </div>
            <div>
              <label className={labelCls}>{t('replace_profile.field_receiver_name')}</label>
              <input type="text" className={inputCls} value={form.receiver_name ?? ''} onChange={(e) => set('receiver_name', e.target.value)} placeholder={t('replace_profile.placeholder_receiver_name')} maxLength={200} />
            </div>
          </div>
        </div>
        <div className="col-span-2 border-t border-gray-100 dark:border-gray-800 pt-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('replace_profile.section_invoicer')}</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t('replace_profile.field_invoicer_gln')}</label>
              <input type="text" className={inputCls} value={form.invoicer_gln ?? ''} onChange={(e) => set('invoicer_gln', e.target.value)} placeholder={t('replace_profile.placeholder_invoicer_gln')} maxLength={50} />
            </div>
            <div>
              <label className={labelCls}>{t('replace_profile.field_invoicer_name')}</label>
              <input type="text" className={inputCls} value={form.invoicer_name ?? ''} onChange={(e) => set('invoicer_name', e.target.value)} placeholder={t('replace_profile.placeholder_invoicer_name')} maxLength={200} />
            </div>
          </div>
        </div>
        <div className="col-span-2 border-t border-gray-100 dark:border-gray-800 pt-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('replace_profile.section_invoicee')}</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t('replace_profile.field_invoicee_gln')}</label>
              <input type="text" className={inputCls} value={form.invoicee_gln ?? ''} onChange={(e) => set('invoicee_gln', e.target.value)} placeholder={t('replace_profile.placeholder_invoicee_gln')} maxLength={50} />
            </div>
            <div>
              <label className={labelCls}>{t('replace_profile.field_invoicee_name')}</label>
              <input type="text" className={inputCls} value={form.invoicee_name ?? ''} onChange={(e) => set('invoicee_name', e.target.value)} placeholder={t('replace_profile.placeholder_invoicee_name')} maxLength={200} />
            </div>
          </div>
        </div>
        <div className="col-span-2 border-t border-gray-100 dark:border-gray-800 pt-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('replace_profile.section_delivery_party')}</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t('replace_profile.field_delivery_party_gln')}</label>
              <input type="text" className={inputCls} value={form.delivery_party_gln ?? ''} onChange={(e) => set('delivery_party_gln', e.target.value)} placeholder={t('replace_profile.placeholder_delivery_party_gln')} maxLength={50} />
            </div>
            <div>
              <label className={labelCls}>{t('replace_profile.field_delivery_party_name')}</label>
              <input type="text" className={inputCls} value={form.delivery_party_name ?? ''} onChange={(e) => set('delivery_party_name', e.target.value)} placeholder={t('replace_profile.placeholder_delivery_party_name')} maxLength={200} />
            </div>
          </div>
        </div>
        <div className="col-span-2 border-t border-gray-100 dark:border-gray-800 pt-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('replace_profile.section_ship_from')}</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t('replace_profile.field_ship_from_gln')}</label>
              <input type="text" className={inputCls} value={form.ship_from_gln ?? ''} onChange={(e) => set('ship_from_gln', e.target.value)} placeholder={t('replace_profile.placeholder_ship_from_gln')} maxLength={50} />
            </div>
            <div>
              <label className={labelCls}>{t('replace_profile.field_ship_from_name')}</label>
              <input type="text" className={inputCls} value={form.ship_from_name ?? ''} onChange={(e) => set('ship_from_name', e.target.value)} placeholder={t('replace_profile.placeholder_ship_from_name')} maxLength={200} />
            </div>
          </div>
        </div>
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500">{t('replace_profile.hint_empty_fields')}</p>
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {loading ? t('common.busy') : submitLabel}
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
          {t('common.cancel')}
        </button>
      </div>
    </form>
  )
}

type XmlTagRow = { labelKey: string; or: string | null; da: string | null; inv: string | null }

const REPLACEMENT_TARGETS: XmlTagRow[] = [
  { labelKey: 'replace_profile.tag_project_number',      or: '<ProjectNumber>',      da: '<ProjectNumber>',      inv: '<ProjectNumber>' },
  { labelKey: 'replace_profile.tag_sender_gln',          or: '<Supplier><GLN>',      da: '<Supplier><GLN>',      inv: '<Supplier><GLN>' },
  { labelKey: 'replace_profile.tag_sender_name',         or: '<Supplier><Name>',     da: '<Supplier><Name>',     inv: '<Supplier><Name>' },
  { labelKey: 'replace_profile.tag_receiver_gln',        or: '<Buyer><GLN>',         da: '<Buyer><GLN>',         inv: '<Buyer><GLN>' },
  { labelKey: 'replace_profile.tag_receiver_name',       or: '<Buyer><Name>',        da: '<Buyer><Name>',        inv: '<Buyer><Name>' },
  { labelKey: 'replace_profile.tag_invoicer_gln',        or: null,                   da: null,                   inv: '<Invoicer><GLN>' },
  { labelKey: 'replace_profile.tag_invoicer_name',       or: null,                   da: null,                   inv: '<Invoicer><Name>' },
  { labelKey: 'replace_profile.tag_invoicee_gln',        or: null,                   da: null,                   inv: '<Invoicee><GLN>' },
  { labelKey: 'replace_profile.tag_invoicee_name',       or: null,                   da: null,                   inv: '<Invoicee><Name>' },
  { labelKey: 'replace_profile.tag_delivery_party_gln',  or: '<DeliveryParty><GLN>', da: '<DeliveryParty><GLN>', inv: '<DeliveryParty><GLN>' },
  { labelKey: 'replace_profile.tag_delivery_party_name', or: '<DeliveryParty><Name>',da: '<DeliveryParty><Name>',inv: '<DeliveryParty><Name>' },
  { labelKey: 'replace_profile.tag_ship_from_gln',       or: '<ShipFrom><GLN>',      da: '<ShipFrom><GLN>',      inv: '<ShipFrom><GLN>' },
  { labelKey: 'replace_profile.tag_ship_from_name',      or: '<ShipFrom><Name>',     da: '<ShipFrom><Name>',     inv: '<ShipFrom><Name>' },
]

function XmlTagCell({ value }: { value: string | null }) {
  if (!value) return <td className="px-4 py-2 text-gray-300 dark:text-gray-600">—</td>
  return <td className="px-4 py-2 font-mono text-indigo-700 dark:text-indigo-300">{value}</td>
}

export function XmlTagsTable() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <span>{t('replace_profile.xml_tags_toggle')}</span>
        <span className="text-gray-400 dark:text-gray-500">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <table className="w-full text-xs">
          <thead>
            <tr className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <th className="text-left px-4 py-2 text-gray-500 dark:text-gray-400 font-medium">{t('replace_profile.col_profile_field')}</th>
              <th className="text-left px-4 py-2 text-gray-500 dark:text-gray-400 font-medium">{t('replace_profile.col_or')}</th>
              <th className="text-left px-4 py-2 text-gray-500 dark:text-gray-400 font-medium">{t('replace_profile.col_da')}</th>
              <th className="text-left px-4 py-2 text-gray-500 dark:text-gray-400 font-medium">{t('replace_profile.col_inv')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {REPLACEMENT_TARGETS.map(row => (
              <tr key={row.labelKey} className="bg-white dark:bg-gray-800">
                <td className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium">{t(row.labelKey)}</td>
                <XmlTagCell value={row.or} />
                <XmlTagCell value={row.da} />
                <XmlTagCell value={row.inv} />
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {open && (
        <p className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-800">
          {t('replace_profile.xml_tags_footnote')}
        </p>
      )}
    </div>
  )
}
